import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import OAuth from '../components/OAuth';
import { signInWithEmailAndPassword, getAuth } from 'firebase/auth';
import { toast } from 'react-toastify';

const galleryImages = [
  'https://img.freepik.com/free-photo/diverse-people-addicted-their-smart-devices-scrolling-through-looking-into-their-screens_23-2151487281.jpg',
  'https://img.freepik.com/free-photo/young-people-relaxing-together_23-2151561954.jpg',
  'https://img.freepik.com/free-photo/young-man-watching-movie-cinema_7502-8384.jpg',
  'https://img.freepik.com/free-photo/view-3d-movie-director-s-chair_23-2151016181.jpg',
  'https://img.freepik.com/free-photo/view-3d-cinema-theatre-room_23-2151067022.jpg',
];

export default function SignIn() {
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [isFocused, setIsFocused] = useState({
    email: false,
    password: false
  });
  const [activeIndex, setActiveIndex] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { email, password } = formData;
  const navigate = useNavigate();

  // Rotate main image every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % 5);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  function onChange(e) {
    setFormData((prevState) => ({
      ...prevState,
      [e.target.id]: e.target.value,
    }));
  }

  function handleFocus(e) {
    setIsFocused(prev => ({
      ...prev,
      [e.target.id]: true
    }));
  }

  function handleBlur(e) {
    if (!e.target.value) {
      setIsFocused(prev => ({
        ...prev,
        [e.target.id]: false
      }));
    }
  }

  // Custom error messages mapping
  const getCustomErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'auth/invalid-email':
        return 'Please enter a valid email address';
      case 'auth/user-disabled':
        return 'This account has been disabled';
      case 'auth/user-not-found':
        return 'No account found with this email';
      case 'auth/wrong-password':
        return 'Incorrect password. Please try again';
      case 'auth/too-many-requests':
        return 'Too many attempts. Please try again later';
      case 'auth/network-request-failed':
        return 'Network error. Please check your connection';
      default:
        return 'Sign in failed. Check your credentials and try again';
    }
  };

  async function onSubmit(e) {
    e.preventDefault();
    setIsLoading(true);
    try {
      const auth = getAuth();
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      if (userCredential) {
        navigate("/");
      }
    } catch (error) {
      const customMessage = getCustomErrorMessage(error.code);
      toast.error(customMessage);
      console.error('Sign in error:', error);
    } finally {
      setIsLoading(false);
    }
  }

  const getImageStyle = (index) => {
    const isMain = index === activeIndex;
    const isHovered = index === hoveredIndex;
    const baseStyle = {
      transition: 'all 0.5s ease',
      borderRadius: '0.75rem',
      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.3)',
      objectFit: 'cover'
    };

    if (isMain) {
      return {
        ...baseStyle,
        width: '100%',
        height: '100%',
        zIndex: 10,
        transform: 'scale(1)',
        filter: 'none',
        border: '1px solid rgba(255, 20, 147, 0.3)'
      };
    }

    const positions = [
      { left: '-5%', top: '15%', width: '25%', height: '25%', zIndex: 3, rotate: '-5deg' },
      { right: '-5%', top: '15%', width: '25%', height: '25%', zIndex: 3, rotate: '5deg' },
      { left: '2%', bottom: '-5%', width: '20%', height: '20%', zIndex: 2, rotate: '-3deg' },
      { right: '2%', bottom: '-5%', width: '20%', height: '20%', zIndex: 2, rotate: '3deg' },
      { left: '-8%', bottom: '10%', width: '15%', height: '15%', zIndex: 1, rotate: '-8deg' },
      { right: '-8%', top: '30%', width: '15%', height: '15%', zIndex: 1, rotate: '8deg' },
      { left: '15%', top: '-5%', width: '12%', height: '12%', zIndex: 0, rotate: '-2deg' },
      { right: '15%', top: '-5%', width: '12%', height: '12%', zIndex: 0, rotate: '2deg' }
    ];

    const posIndex = index < activeIndex ? index : index - 1;
    const position = positions[posIndex % positions.length];

    return {
      ...baseStyle,
      position: 'absolute',
      ...position,
      transform: `scale(${isHovered ? 1.05 : 0.85}) rotate(${isHovered ? position.rotate * 1.5 : position.rotate}deg)`,
      filter: isHovered ? 'brightness(1.1) saturate(1.1)' : 'brightness(0.7)',
      opacity: isHovered ? 1 : 0.8,
      cursor: 'pointer',
      border: '1px solid rgba(255, 255, 255, 0.1)'
    };
  };

  return (
    <section className="bg-black min-h-screen relative overflow-hidden">
      {/* Floating movie elements */}
      <div className="absolute top-0 left-0 w-full h-full opacity-3 pointer-events-none">
        {[...Array(10)].map((_, i) => (
          <div 
            key={i}
            className="absolute bg-white rounded-full"
            style={{
              width: `${Math.random() * 6 + 2}px`,
              height: `${Math.random() * 6 + 2}px`,
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animation: `float ${Math.random() * 12 + 8}s linear infinite`,
              animationDelay: `${Math.random() * 3}s`,
              opacity: Math.random() * 0.3 + 0.1
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-4 pt-4 pb-8">
        <h1 className="text-3xl md:text-4xl text-center font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#FF1493] via-[#FF8C00] to-[#FFD700] animate-gradient-x">
          Welcome Back!
        </h1>
        <p className="text-center text-gray-400 mt-1 text-sm md:text-base">
          Sign in to continue to your movie haven.
        </p>

        <div className="flex flex-col lg:flex-row justify-center items-center gap-6 mt-6">
          {/* Image Gallery Container */}
          <div className="w-full lg:w-1/2 relative h-[280px] md:h-[380px] lg:h-[480px] mb-6 lg:mb-0">
            <div className="absolute inset-0 overflow-hidden rounded-xl">
              {galleryImages.map((img, index) => (
                <img
                  key={index}
                  src={img}
                  alt={`Cinema scene ${index + 1}`}
                  className="absolute transition-all duration-500 ease-out"
                  style={getImageStyle(index)}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  onClick={() => setActiveIndex(index)}
                />
              ))}
            </div>
          </div>

          {/* Sign In Form */}
          <div className="w-full lg:w-1/2 lg:max-w-md">
            <form onSubmit={onSubmit} className="bg-gray-900 bg-opacity-70 backdrop-blur-sm p-5 rounded-xl border border-gray-800">
              <div className="relative mb-4">
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={onChange}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  className="w-full px-4 py-3 text-white bg-gray-800 border border-gray-700 rounded-lg transition ease-in-out focus:outline-none focus:ring-2 focus:ring-[#FF1493]"
                />
                <label 
                  htmlFor="email"
                  className={`absolute left-4 transition-all duration-200 ease-in-out pointer-events-none ${isFocused.email || email ? 'top-1 text-xs text-gray-400' : 'top-3.5 text-gray-500'}`}
                >
                  Email address
                </label>
              </div>
              <div className="relative mb-4">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={password}
                  onChange={onChange}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  className="w-full px-4 py-3 text-white bg-gray-800 border border-gray-700 rounded-lg transition ease-in-out focus:outline-none focus:ring-2 focus:ring-[#FF1493]"
                />
                <label 
                  htmlFor="password"
                  className={`absolute left-4 transition-all duration-200 ease-in-out pointer-events-none ${isFocused.password || password ? 'top-1 text-xs text-gray-400' : 'top-3.5 text-gray-500'}`}
                >
                  Password
                </label>
                <span 
                  className="absolute right-3 top-3 text-sm cursor-pointer text-gray-400 hover:text-white"
                  onClick={() => setShowPassword((prevState) => !prevState)}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </span>
              </div>
              <div className="flex justify-between text-sm mb-5">
                <p className="text-gray-400">
                  Don't have an account?
                  <Link
                    to="/sign-up"
                    className="text-[#FF1493] hover:text-[#1FA9FF] transition duration-200 ease-out ml-1"
                  >
                    Register
                  </Link>
                </p>
                <p className="text-gray-400 hover:text-[#FF1493] transition duration-200 ease-out">
                  <Link to="/forgot-password">Forgot password?</Link>
                </p>
              </div>
              <button
                className="w-full text-white px-5 py-3 text-sm font-bold uppercase rounded-full shadow-lg transition duration-150 ease-in-out hover:shadow-xl bg-gradient-to-r from-[#FF1493] via-[#FF8C00] to-[#FFD700] animate-gradient-x flex items-center justify-center"
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Aweit AI Verifying...
                  </>
                ) : 'Sign Me In'}
              </button>
              <div className="flex items-center my-4 before:border-t before:flex-1 before:border-gray-700 after:border-t after:flex-1 after:border-gray-700">
                <p className="text-center font-semibold mx-4 text-gray-400 text-sm">OR</p>
              </div>
              <OAuth />
            </form>
          </div>
        </div>
      </div>

      {/* CSS Animations */}
      <style jsx global>{`
        @keyframes float {
          0% { transform: translateY(0) rotate(0deg); opacity: 0.1; }
          50% { opacity: 0.2; }
          100% { transform: translateY(-100vh) rotate(180deg); opacity: 0; }
        }
        @keyframes gradient-x {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .animate-gradient-x {
          background-size: 200% 200%;
          animation: gradient-x 3s ease infinite;
        }
      `}</style>
    </section>
  );
}